import { signal } from "@preact/signals";
import { verifyPin, type User, type VenueId } from "@orderman/shared";
import { settings } from "./config.js";
import { kvGet, kvSet } from "./idb.js";

/**
 * Anmeldung der Kellner. Personal wird vom Server geladen und lokal gecacht
 * (Offline-Login). PIN-Prüfung lokal über den (nicht-kryptografischen) Hash.
 */

const LS_USER = "orderman.currentUser";

function loadStoredUser(): User | null {
  try {
    const s = localStorage.getItem(LS_USER);
    return s ? (JSON.parse(s) as User) : null;
  } catch {
    return null;
  }
}

export const currentUser = signal<User | null>(loadStoredUser());
export const users = signal<User[]>([]);
export const usersLoading = signal(false);

const usersKey = (v: VenueId) => `users:${v}`;

/** Personal laden: zuerst Cache (offline-fähig), dann — wenn online — vom Server. */
export async function loadUsers(): Promise<void> {
  usersLoading.value = true;
  const v = settings.value.venueId;
  const cached = await kvGet<User[]>(usersKey(v));
  if (cached) users.value = cached;
  try {
    const res = await fetch(`${settings.value.serverUrl}/api/users?venue=${v}`);
    if (res.ok) {
      const list = (await res.json()) as User[];
      users.value = list;
      await kvSet(usersKey(v), list);
    }
  } catch {
    /* offline → Cache genügt */
  }
  usersLoading.value = false;
}

/** Personal direkt setzen (Voll-Cloud-Modus: aus dem Bootstrap) + cachen. */
export async function setUsers(list: User[]): Promise<void> {
  users.value = list;
  await kvSet(usersKey(settings.value.venueId), list);
}

/** Anmelden. Gibt false bei falschem PIN / inaktivem Konto. */
export function login(user: User, pin: string): boolean {
  if (!user.isActive || !verifyPin(pin, user.pinHash)) return false;
  currentUser.value = user;
  localStorage.setItem(LS_USER, JSON.stringify(user));
  return true;
}

export function logout(): void {
  currentUser.value = null;
  localStorage.removeItem(LS_USER);
}
